import React, { useCallback, useEffect, useState } from "react";
import { Box, Fab } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import axios from "../../utils/axios.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import MemoryCard from "./MemoryCard.tsx";
import TilePage from "../common/TilePage.tsx";

interface MemoriesPageProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

interface MemoryItem {
  id: string;
  memory: string;
}

const MemoriesPage: React.FC<MemoriesPageProps> = ({
  openDrawer = false,
  setOpenDrawer,
}) => {
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const fetchMemories = useCallback(async () => {
    const response = await axios.get("/v1/memory/");
    setMemories(response.data.objects || []);
  }, []);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  const handleCreateMemory = async (memoryText: string) => {
    await axios.post("/v1/memory/", JSON.stringify({ content: memoryText }));
    setIsCreateDialogOpen(false);
    fetchMemories();
  };

  return (
    <>
      <TilePage
        title={<Trans context="Memories page title">Memories</Trans>}
        subtitle={
          <Trans context="Memories page subtitle">
            Add information to Tally's long-term memory so it can reference them
            in future responses.
          </Trans>
        }
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        action={
          <Fab
            color="primary"
            aria-label={i18n._(
              msg({
                context:
                  "Accessibility label for creating a memory from memories page",
                message: "Create memory",
              }),
            )}
            onClick={() => setIsCreateDialogOpen(true)}
            sx={{
              position: "absolute",
              right: { xs: 16, sm: 24, md: 28 },
              bottom: { xs: 16, sm: 24, md: 28 },
            }}
          >
            <AddIcon />
          </Fab>
        }
      >
        {memories.map((memory) => (
          <Box key={memory.id}>
            <MemoryCard
              id={memory.id}
              memory={memory.memory}
              onMemoryUpdated={fetchMemories}
            />
          </Box>
        ))}
      </TilePage>

      <TallyDialog
        open={isCreateDialogOpen}
        title={i18n._(
          msg({
            context: "Button title enabling the user to create a memory",
            message: "Create a memory",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Header with explanation what creating a memory does in the application.",
            message:
              "By creating a memory, I will learn the information so I can use it going forward in our conversations",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title for creation of a memory",
            message: "Create",
          }),
        )}
        onSubmit={handleCreateMemory}
        initialInputValue=""
        handleDialogClose={() => setIsCreateDialogOpen(false)}
      />
    </>
  );
};

export default MemoriesPage;
